import { Tooltip } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import Swal from 'sweetalert2';

const API_KEY = 'AIzaSyCI0u9Yx3LlcSV5yMlXQbU3NCuA9Llv9YY';

// Extend MediaRecorder to carry our chunks array
type MediaRecorderWithChunks = MediaRecorder & {
    chunks: Blob[];
};

export type SpeechToTextInputProps = {
    disabled?: boolean;
    onTranscripting?: (transcripting: boolean) => void;
    onTranscript?: (transcript: string) => void;
};

export function SpeechToTextInput(props: SpeechToTextInputProps) {
    const { disabled, onTranscripting, onTranscript } = props;

    const streamRef = useRef<MediaStream | null>(null);
    const mediaRef = useRef<MediaRecorderWithChunks | null>(null);
    const [recording, setRecording] = useState(false);

    const start = async (): Promise<void> => {
        setRecording(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream) as MediaRecorderWithChunks;
            recorder.chunks = [];

            recorder.ondataavailable = (e: BlobEvent) => {
                if (e.data.size > 0) {
                    recorder.chunks.push(e.data);
                }
            };

            recorder.start();

            streamRef.current = stream;
            mediaRef.current = recorder;
        } catch (error) {
            console.log('error ocurred', error);
        }
    };

    const stop = async (): Promise<void> => {
        setRecording(false);

        const recorder = mediaRef.current;
        if (!recorder) return;

        recorder.onstop = async () => {
            // Combine chunks and convert to Base64
            const blob = new Blob(recorder.chunks, { type: 'audio/wav' });
            const base64 = await blobToBase64(blob);

            // Send to Google Speech-to-Text
            try {
                onTranscripting?.(true);

                const transcript = await transcribeAudio(base64);
                if (!transcript) return;

                onTranscript?.(transcript);
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'เกิดข้อผิดพลาด',
                    text: 'ไม่สามารถแปลงเสียงเป็นข้อความได้'
                });
            } finally {
                onTranscripting?.(false);
            }
        };

        recorder.stop();
        streamRef.current?.getTracks().forEach(track => track.stop());
    };

    return (
        <Tooltip
            isOpen={recording}
            label="กดอีกครั้งเมื่อพูดเสร็จ"
            placement="top"
        >
            <button
                disabled={disabled}
                onClick={!recording ? start : stop}
                className="rounded-lg bg-primary-900 px-4 py-2 font-medium hover:bg-primary-700"
            >
                {recording ? <FaMicrophoneSlash /> : <FaMicrophone />}
            </button>
        </Tooltip>
    );
}

/**
 * Convert a Blob to a Base64 string (without the data: prefix)
 */
async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            // Strip off "data:...;base64,"
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = err => reject(err);
        reader.readAsDataURL(blob);
    });
}

/**
 * Call Google Cloud Speech-to-Text REST API
 */
interface SpeechConfig {
    encoding?: string;
    sampleRateHertz?: number;
    languageCode: string; // primary language
    alternativeLanguageCodes?: string[]; // extra languages to detect
}

interface SpeechRequest {
    config: SpeechConfig;
    audio: { content: string };
}

interface SpeechResponse {
    results?: Array<{
        alternatives: Array<{ transcript: string }>;
    }>;
}

async function transcribeAudio(base64Audio: string) {
    const url = `https://speech.googleapis.com/v1/speech:recognize?key=${API_KEY}`;
    const body: SpeechRequest = {
        config: {
            // encoding: 'LINEAR16',
            // sampleRateHertz: 44100,
            languageCode: 'th-TH',
            alternativeLanguageCodes: ['en-US']
        },
        audio: { content: base64Audio }
    };

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    });
    const data = (await res.json()) as SpeechResponse;

    console.log('Google Speech Response:', data);

    return data.results?.[0]?.alternatives[0]?.transcript ?? '';
}
